import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { InputModule } from '@ui-components/controls/input/input.module';

type SummaryBoardTasksModalData = {
  title: string;
  boards?: Array<{ id: number; name: string }>;
};

@Component({
  selector: 'app-summary-board-tasks-modal',
  standalone: true,
  templateUrl: './summary-board-tasks-modal.component.html',
  styleUrls: ['./summary-board-tasks-modal.component.scss'],
  imports: [MatDialogModule, MatButtonModule, ReactiveFormsModule, InputModule, IconComponent, NgIf, NgFor],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryBoardTasksModalComponent extends PuSubscribable implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<SummaryBoardTasksModalComponent>);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  public readonly data = inject<SummaryBoardTasksModalData>(MAT_DIALOG_DATA);

  EIcon = EIcon;
  EColorPalette = EColorPalette;

  searchControl = new FormControl('');
  filteredBoards: Array<{ id: number; name: string }> = [];

  ngOnInit(): void {
    this.filterBoards('');
    this.searchControl.valueChanges
      .pipe(startWith(''), debounceTime(200), distinctUntilChanged())
      .subscribe(searchStr => {
        this.filterBoards(searchStr ?? '');
      })
      .untilDestroyed(this);
  }

  private getSortedBoards(): Array<{ id: number; name: string }> {
    const boards = this.data?.boards ?? [];
    return [...boards].sort((a, b) => a.name.localeCompare(b.name));
  }

  private filterBoards(search?: string): void {
    const normalized = (search || '').trim().toLowerCase();
    const boards = this.getSortedBoards();

    if (!normalized) {
      this.filteredBoards = boards;
    } else {
      this.filteredBoards = boards.filter(board => board.name?.toLowerCase().includes(normalized));
    }

    this.cdr.markForCheck();
  }

  trackById(_index: number, board: { id: number }): number {
    return board.id;
  }

  getBoardUrl(boardId: number): string {
    return this.router.serializeUrl(this.router.createUrlTree([`${AppRoutes.BOARDS}/${boardId}`]));
  }

  close(): void {
    this.dialogRef.close();
  }
}
